import { FormControl, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import FormMonitoring from 'components/FormMonitoring';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppColors } from 'resources/AppColors';
import CustomDialog from 'components/Dialogs/MasterDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import DatePickerMonitoring from 'components/DatePickers/DatePickerMonitoring/DatePickerMonitoring';
import { CommonErrorsTexts, notesTexts } from 'locale/es';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';

const useStyles = makeStyles((theme) => ({
  textFieldLabel: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: AppColors.PRIMARY,
      },
    },
    '& .MuiInputBase-root': {
      color: AppColors.PRIMARY,
    },
    '& .MuiInputLabel-root': {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
}));

const newNoteSchema = z
  .object({
    text: z
      .string({ required_error: CommonErrorsTexts.required })
      .min(2, notesTexts.formInputErrors.note),
    noteDate: z.date({
      errorMap: (issue, { defaultError }) => ({
        message:
          issue.code === 'invalid_date'
            ? CommonErrorsTexts.date.invalidDate
            : issue.code === 'invalid_type'
            ? CommonErrorsTexts.date.date
            : defaultError,
      }),
    }),
  })
  .transform((data) => ({
    ...data,
    noteDate: data.noteDate
      ? format(data.noteDate, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
      : null,
    // noteDate: format(data.noteDate, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
  }));

function NoteForm({ onSubmit, isEdit }) {
  const { entity } = useLocation().state

  const entityId = entity?.id;

  const navigate = useNavigate();

  const [formError, setFormError] = useState('');

  const classes = useStyles();

  const dispatch = useDispatch();

  const { handleSubmit, control, reset } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: zodResolver(newNoteSchema),
    resetOptions: {
      keepDefaultValues: true,
    },
    defaultValues: {
      text: entity?.text || '',
      noteDate: entity?.noteDate ? new Date(entity?.noteDate) : new Date(),
    },
  });

  async function handleOnConfirmSubmit(data) {
    try {

      const noteObject = {
        ...data,
        [isEdit ? "id" : "relatedTo"]: entityId
      }
  
      await dispatch(onSubmit(noteObject));
      handleShowAddModal();
    } catch (error) {
      setFormError(error);
    }
  }

  function handleShowAddModal() {
    navigate('/notes');
  }

  return (
    <CustomDialog
      title={notesTexts.CREATE}
      show={true}
      handleShow={handleShowAddModal}
    >
      <FormMonitoring
        submitForm={handleSubmit(handleOnConfirmSubmit)}
        resetFormInputs={reset}
        isEdit={isEdit}
      >
        <Controller
          control={control}
          name='noteDate'
          render={({
            field: { onChange, onBlur, value, ref },
            fieldState: { error },
          }) => (
            <FormControl
              sx={{ mt: 2, mb: 1 }}
              className={classes.textFieldLabel}
            >
              <DatePickerMonitoring
                label={notesTexts.DATE}
                maxDate={new Date()}
                inputRef={ref}
                value={value}
                setValue={onChange}
                required={true}
                textfieldProps={{
                  error: Boolean(error),
                  helperText: error?.message ?? '',
                  onBlur: onBlur,
                }} />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name='text'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              InputLabelProps={{
                required: true,
              }}
              label={notesTexts.NOTE}
              margin='normal'
              variant='outlined'
              multiline
              minRows={3}
              autoFocus
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message ?? ''} />
          )}
        />
        <Typography variant='body' sx={{ color: "red" }}>
          {formError && notesTexts.formInputErrors.api_error}
        </Typography>
      </FormMonitoring>
    </CustomDialog>
  );
}

export default NoteForm;
